/**
 * The **narrative backdrop** read of a codex entry (`narrative-backdrop`): which entries the GM may
 * pin behind a narrative scene, and the lines the stage prints for one. Everything here comes from
 * the served codex entry, which every player can already read, so nothing on the stage is a secret.
 */
import type { CodexEntry } from "./types";

/** A backdrop is a world: only `place` entries can be pinned. */
export function isBackdropEntry(entry: CodexEntry): boolean {
  return entry.kind === "place";
}

/** What the stage prints for a pinned entry. */
export type BackdropReadout = {
  /** The hero line: the entry's name. */
  name: string;
  /** The formal chart designation (`C-621c · CHARTER AC-2`), or null when the chart records none. */
  designation: string | null;
  /**
   * Short location lines (`RING 10`, `CASCADE LINE`), else the role and affiliation. The star
   * (`STAR C-621`) is left out when the designation already names it (`C-621c`).
   */
  details: string[];
};

/**
 * Build the stage's lines for an entry. The location lines are parsed from a fact that names the
 * star and the ring (`…star C-621, Ring 10 // Cascade Line.`); an entry without one falls back to
 * its `role` and `affiliation` header fields.
 */
export function backdropReadout(entry: CodexEntry): BackdropReadout {
  const designation = entry.chart?.designation ?? null;
  return {
    name: entry.name,
    designation,
    details: starAndRing(entry, designation) ?? [entry.role, entry.affiliation].filter((s): s is string => s !== undefined && s !== ""),
  };
}

const STAR = /\bstar ([A-Z0-9][A-Z0-9-]*)/i;
const RING = /\bRing (\d+)(?:\s*\/\/\s*([^.,;]+))?/;

function starAndRing(entry: CodexEntry, designation: string | null): string[] | null {
  for (const fact of entry.facts) {
    const star = STAR.exec(fact.text);
    const ring = RING.exec(fact.text);
    if (star === null || ring === null) continue;
    const starId = star[1]!.toUpperCase();
    const lines = designation?.toUpperCase().includes(starId) ? [] : [`STAR ${starId}`];
    lines.push(`RING ${ring[1]}`);
    const line = ring[2]?.trim();
    if (line) lines.push(line.toUpperCase());
    return lines;
  }
  return null;
}
